// src/components/TechnicalMonitor.jsx
// 보유종목 기본 기술적 신호 모니터 — 엔벨로프·이평선·채널·추세선. 일봉 1년, 클라이언트 계산(0토큰).
import React, { useEffect, useMemo, useState } from "react";
import { freshSignals } from "../lib/technicals";

const DIR = { up: "#c0392b", down: "#2563eb", flat: "#b8860b" };

export default function TechnicalMonitor({ holdings }) {
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(false);

  const items = useMemo(() => (holdings || []).map((h) => ({
    ticker: h.ticker, market: h.market || "KR", name: h.name || String(h.ticker),
    short: String(h.side || "Long").toLowerCase().startsWith("s"),
  })).filter((x) => x.ticker), [holdings]);

  const symKey = items.map((x) => `${x.ticker}:${x.market}`).join(",");
  useEffect(() => {
    if (!symKey) { setSeries(null); return; }
    let alive = true;
    setLoading(true);
    fetch(`/api/history?symbols=${encodeURIComponent(symKey)}&range=1y&interval=1d`)
      .then((r) => r.json())
      .then(({ series: s }) => { if (alive) setSeries(s || {}); })
      .catch(() => { if (alive) setSeries({}); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [symKey]);

  const rows = useMemo(() => {
    if (!series) return [];
    return items.map((x) => {
      const pts = series[x.ticker]?.points;
      if (!pts) return null;
      const sigs = freshSignals(pts.map((p) => p[1]));
      if (!sigs || !sigs.length) return null;
      return { ...x, sigs, freshest: sigs[0].ago };
    }).filter(Boolean).sort((a, b) => a.freshest - b.freshest);   // 오늘 신규 종목 먼저
  }, [series, items]);

  return (
    <div style={{ marginTop: 12, background: "#fbfbfd", border: "1px solid #e8e8ee", borderRadius: 10, padding: "12px 16px" }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#1a1a2e", marginBottom: 8 }}>
        📐 기술적 신호 모니터 <span style={{ fontWeight: 500, color: "#98a2b3" }}>(신규 발생 · 최근 6거래일 · 🔦=오늘 신규)</span>
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: "#aaa" }}>불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: "#bbb" }}>최근 신규 신호 없음</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {rows.map((r) => (
            <div key={String(r.ticker)} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: r.short ? "#c0392b" : "#0a7c59", width: 12, flexShrink: 0 }}>{r.short ? "S" : "L"}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#1a1a2e", minWidth: 92 }}>{r.name}</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {r.sigs.map((s, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: DIR[s.dir], borderRadius: 999, padding: "2px 8px", opacity: s.ago === 0 ? 1 : 0.55 }}>
                    {s.ago === 0 && <span style={{ marginRight: 2 }}>🔦</span>}{s.t}{s.ago > 0 && <span style={{ marginLeft: 3, fontWeight: 500, opacity: 0.9 }}>D-{s.ago}</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 10.5, color: "#aaa", marginTop: 8 }}>
        상태가 새로 바뀐 신호만(엔벨·이평·채널·추세선). 🔦=오늘 발생 · D-n=n거래일 전 발생 · 페이지 열 때마다 일봉 기준 자동 갱신
      </div>
    </div>
  );
}
